import { isApiError } from "@positive/api-client";
import { useErrorText } from "@positive/ui";
import { BusyIndicator, Button, MessageStrip, Title } from "@ui5/webcomponents-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type { AuthState } from "./authService";
import { BranchSwitchDialog } from "./BranchSwitchDialog";
import { useIdentityBootstrap } from "./IdentityProvider";
import "./identity.css";

function AuthPage({ testId, children }: { testId: string; children: ReactNode }) {
  const { t } = useTranslation("shell");
  return (
    <main className="positive-auth-page" data-testid={testId}>
      <div className="positive-auth-card">
        <Title level="H3">{t("productTitle")}</Title>
        {children}
      </div>
    </main>
  );
}

function Busy({ label, testId }: { label: string; testId: string }) {
  return (
    <AuthPage testId={testId}>
      <BusyIndicator active delay={0} />
      <span>{label}</span>
    </AuthPage>
  );
}

const testIds = {
  authBusy: "auth-busy",
  signedOut: "signed-out",
  authError: "auth-error",
  consoleDenied: "console-denied",
  sessionError: "session-error",
  sessionBusy: "session-busy",
  branchSelection: "branch-selection",
} as const;

/** Error codes for which a known message exists in `shell:auth.errors`. */
const authErrorCodes = new Set(["access_denied", "temporarily_unavailable", "login_required", "interaction_required"]);

function authErrorKey(state: Extract<AuthState, { status: "error" }>) {
  return authErrorCodes.has(state.code) ? (`auth.errors.${state.code}` as const) : ("auth.errors.generic" as const);
}

/**
 * Renders the app only when the user is signed in and — in the tenant app — the session is loaded and a branch is
 * selected (P12). Shows sign-in progress, sign-in errors (also those surfaced by Keycloak), the signed-out page and
 * session errors (`user_disabled`, `tenant_mismatch`, suspended tenant …) otherwise. The account's language is applied
 * by `AccountPreferences` once the session is known (ADR-0009/D-6, decision I6).
 */
export function SessionGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation("shell");
  const describeError = useErrorText();
  const { identity, sessionStatus, sessionError, retrySession } = useIdentityBootstrap();
  const { authState, auth, session, mode, access } = identity;

  switch (authState.status) {
    case "starting":
    case "redirecting":
    case "callback":
      return <Busy label={t("auth.signingIn")} testId={testIds.authBusy} />;
    case "signed-out":
      return (
        <AuthPage testId={testIds.signedOut}>
          <MessageStrip hideCloseButton design={authState.reason === "manual" ? "Information" : "Critical"}>
            {t(`auth.signedOut.${authState.reason}`)}
          </MessageStrip>
          <Button design="Emphasized" onClick={() => void auth.signIn("/")} data-testid="sign-in">
            {t("auth.signIn")}
          </Button>
        </AuthPage>
      );
    case "error":
      return (
        <AuthPage testId={testIds.authError}>
          <MessageStrip hideCloseButton design="Negative">
            {t(authErrorKey(authState))}
          </MessageStrip>
          <Button design="Emphasized" onClick={() => void auth.signIn("/")} data-testid="sign-in">
            {t("auth.signInAgain")}
          </Button>
        </AuthPage>
      );
    case "authenticated":
      break;
  }

  if (mode.kind === "console") {
    if (!access.isOperator) {
      return (
        <AuthPage testId={testIds.consoleDenied}>
          <MessageStrip hideCloseButton design="Negative">
            {t("auth.operatorOnly")}
          </MessageStrip>
          <Button onClick={() => void auth.signOut()} data-testid="sign-out">
            {t("user.signOut")}
          </Button>
        </AuthPage>
      );
    }
    return children;
  }

  if (sessionStatus === "error") {
    const code = isApiError(sessionError) ? sessionError.code : undefined;
    return (
      <AuthPage testId={testIds.sessionError}>
        <MessageStrip hideCloseButton design="Negative">
          {describeError(sessionError).message}
        </MessageStrip>
        <div className="positive-auth-actions">
          {code !== "user_disabled" && code !== "tenant_mismatch" && (
            <Button onClick={retrySession} data-testid="session-retry">
              {t("auth.retry")}
            </Button>
          )}
          <Button design="Emphasized" onClick={() => void auth.signOut()} data-testid="sign-out">
            {t("user.signOut")}
          </Button>
        </div>
      </AuthPage>
    );
  }
  if (!session) return <Busy label={t("auth.loadingSession")} testId={testIds.sessionBusy} />;
  if (session.session.selectionRequired || (session.session.branchId === null && session.branches.length > 0)) {
    return (
      <AuthPage testId={testIds.branchSelection}>
        <BranchSwitchDialog open required session={session} onClose={() => undefined} />
      </AuthPage>
    );
  }
  return children;
}
